// src/components/OfficialCardDirectory.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from './shared/Button';
import Input from './shared/Input';
import Select from './shared/Select';
import './OfficialCardDirectory.css';

// "A, B, and C" style join, matching whatever decks the API currently reports
const formatDeckList = (decks) => {
  if (decks.length === 0) return 'standard decks';
  if (decks.length === 1) return decks[0];
  return `${decks.slice(0, -1).join(', ')}, and ${decks[decks.length - 1]}`;
};

// Small fixed palette so any deck (including future ones) gets a distinct,
// consistent color without hardcoding per-deck-name CSS classes. Assigned by
// position in the known deck list (collision-free up to palette length)
// rather than a text hash, which can coincidentally collide two deck names.
const DECK_BADGE_COLORS = ['deck-color-1', 'deck-color-2', 'deck-color-3', 'deck-color-4'];
const deckBadgeClass = (deckName, availableDecks) => {
  const index = availableDecks.indexOf(deckName);
  const safeIndex = index === -1 ? 0 : index;
  return DECK_BADGE_COLORS[safeIndex % DECK_BADGE_COLORS.length];
};

const OfficialCardDirectory = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [currentChallenge, setCurrentChallenge] = useState(null);
  const [availableDecks, setAvailableDecks] = useState([]);

  // Generate simple math challenge
  const generateMathChallenge = () => {
    const num1 = Math.floor(Math.random() * 9) + 1; // 1-9
    const num2 = Math.floor(Math.random() * 9) + 1; // 1-9
    const correctAnswer = num1 + num2;

    // Generate wrong answers that are plausible
    const wrongAnswers = [];
    wrongAnswers.push(correctAnswer - 1);
    wrongAnswers.push(correctAnswer + 1);
    wrongAnswers.push(correctAnswer + 2);

    // Mix up the answers
    const allAnswers = [correctAnswer, ...wrongAnswers];
    const shuffled = allAnswers.sort(() => Math.random() - 0.5);
    const correctIndex = shuffled.indexOf(correctAnswer);

    return {
      question: `What is ${num1} + ${num2}?`,
      answers: shuffled,
      correct: correctIndex,
    };
  };

  // Initialize challenge on mount
  useEffect(() => {
    setCurrentChallenge(generateMathChallenge());
  }, []);

  // Add noindex meta tag for SEO blocking
  useEffect(() => {
    const metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    metaRobots.content = 'noindex, nofollow';
    document.head.appendChild(metaRobots);

    // Cleanup on unmount
    return () => {
      document.head.removeChild(metaRobots);
    };
  }, []);

  // Fetch cards from API
  const fetchCards = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        search: searchTerm,
        type: typeFilter,
        page: currentPage,
        limit: 50,
      });

      const serverURL =
        process.env.REACT_APP_API_URL ||
        process.env.REACT_APP_BACKEND_URL ||
        (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:3001');

      const response = await fetch(`${serverURL}/api/official-cards?${params}`);

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('Too many requests. Please slow down and try again in a minute.');
        }
        throw new Error('Failed to load card directory');
      }

      const data = await response.json();

      // If we get no cards on a page > 1, reset to page 1
      if (data.cards && data.cards.length === 0 && currentPage > 1) {
        setCurrentPage(1);
        return;
      }

      setCards(data.cards || []);
      setPagination(data.pagination || null);
      if (data.filters?.availableDecks) {
        setAvailableDecks(data.filters.availableDecks);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching cards:', err);
      // On error, offer to go back to page 1
      if (currentPage > 1) {
        setError(`${err.message}. Try going back to the first page.`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount and when filters/page change (only if verified)
  useEffect(() => {
    if (isVerified) {
      fetchCards();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, typeFilter, isVerified]);

  // Handle search submission
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to page 1 when searching
    fetchCards();
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle type filter change
  const handleTypeFilterChange = (e) => {
    setTypeFilter(e.target.value);
    setCurrentPage(1); // Reset to page 1 when changing filter
  };

  // Prevent right-click
  const handleContextMenu = (e) => {
    e.preventDefault();
  };

  // Handle verification answer
  const handleVerificationAnswer = (answerIndex) => {
    if (!currentChallenge) return;

    if (answerIndex === currentChallenge.correct) {
      setIsVerified(true);
      setVerificationError('');
    } else {
      setVerificationError('Incorrect answer. Please try again!');
      // Get a new challenge after wrong answer
      setTimeout(() => {
        setCurrentChallenge(generateMathChallenge());
        setVerificationError('');
      }, 1500);
    }
  };

  return (
    <div className="card-directory-container" onContextMenu={handleContextMenu}>
      <div className="card-directory-header">
        <h1>🃏 Standard Card Directory</h1>
        <p className="subtitle">Carnivores Adapting Terribly</p>
        <div className="warning-banner">
          <strong>⚠️ For Verification Purposes Only</strong>
          <p>
            This directory lists all standard deck cards. Use this to verify card authenticity and
            distinguish standard content from user-generated content.
          </p>
        </div>
      </div>

      {!isVerified && currentChallenge ? (
        <div className="verification-challenge">
          <h2>🔒 Verify You're Human</h2>
          <p className="verification-subtitle">
            Please solve this simple math problem to access the standard card directory
          </p>
          <div className="challenge-box">
            <p className="challenge-question">{currentChallenge.question}</p>
            <div className="challenge-answers">
              {currentChallenge.answers.map((answer, index) => (
                <Button
                  key={index}
                  variant="secondary"
                  onClick={() => handleVerificationAnswer(index)}
                  className="challenge-answer-btn"
                >
                  {answer}
                </Button>
              ))}
            </div>
            {verificationError && <p className="verification-error">{verificationError}</p>}
          </div>
        </div>
      ) : isVerified ? (
        <>
          <div className="explanation-section">
            <h2>Understanding Card IDs</h2>
            <div className="card-id-explanation">
              <div className="id-format">
                <strong>Standard Deck Cards:</strong>
                <code>
                  {typeFilter === 'white' || typeFilter === 'all'
                    ? 'white_123_ABC...'
                    : 'black_456_XYZ...'}
                </code>
                <p>Cards from {formatDeckList(availableDecks)} decks</p>
              </div>
              <div className="id-format">
                <strong>Write-In Cards:</strong>
                <code>write-in-white-1234567890-abc...</code>
                <p>⚠️ USER-GENERATED content created during gameplay (not stored on our servers)</p>
              </div>
              <div className="id-format">
                <strong>Custom Deck Cards:</strong>
                <code>custom_white_deck_1234567890_5</code>
                <p>⚠️ THIRD-PARTY content uploaded by users (not stored on our servers)</p>
              </div>
            </div>
            <p className="legal-note">
              <strong>Note:</strong> Only standard deck cards ({formatDeckList(availableDecks)}) can
              be verified in this directory. User-generated and custom deck cards are ephemeral,
              exist only during game sessions, and are the sole responsibility of the user who
              created or uploaded them.
            </p>
          </div>

          <div className="card-directory-controls">
            <form onSubmit={handleSearch} className="search-form">
              <Input
                type="text"
                placeholder="Search by card ID or text..."
                value={searchTerm}
                onChange={handleSearchChange}
                ariaLabel="Search cards"
              />
              <Button type="submit" variant="primary" className="search-button">
                🔍 Search
              </Button>
            </form>

            <div className="filters">
              <label htmlFor="type-filter">Card Type:</label>
              <Select
                id="type-filter"
                value={typeFilter}
                onChange={handleTypeFilterChange}
                options={[
                  { value: 'all', label: 'All Cards' },
                  { value: 'black', label: 'Question Cards Only' },
                  { value: 'white', label: 'Answer Cards Only' },
                ]}
                ariaLabel="Filter by card type"
              />
            </div>
          </div>

          {pagination && (
            <div className="stats">
              Total Standard Cards: <strong>{pagination.totalCards}</strong>
              {searchTerm && ` (filtered results)`}
            </div>
          )}

          {loading && <div className="loading">Loading cards...</div>}

          {error && <div className="error-message">{error}</div>}

          {!loading && !error && cards.length === 0 && (
            <div className="no-results">
              <p>No cards found matching your search.</p>
              <Button
                variant="secondary"
                onClick={() => {
                  setSearchTerm('');
                  setCurrentPage(1);
                  fetchCards();
                }}
                className="reset-button"
              >
                Clear Search
              </Button>
            </div>
          )}

          {!loading && !error && cards.length > 0 && (
            <>
              <div className="cards-grid">
                {cards.map((card) => (
                  <div key={card.id} className={`card-item card-item-${card.type}`}>
                    <div className="card-type-badge">{card.type === 'black' ? '❓ Q' : '💬 A'}</div>
                    <div className="card-text">
                      <span>{card.text}</span>
                      {card.pick > 1 && (
                        <span className="card-pick-indicator">Pick {card.pick}</span>
                      )}
                    </div>
                    <div className="card-metadata">
                      <div
                        className={`card-deck-badge ${deckBadgeClass(card.deck, availableDecks)}`}
                      >
                        {card.deck}
                      </div>
                      <div className="card-id">{card.id}</div>
                    </div>
                  </div>
                ))}
              </div>

              {pagination && pagination.totalPages > 1 && (
                <div className="pagination">
                  <Button
                    variant="ghost"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={!pagination.hasPrevious}
                    className="pagination-button"
                  >
                    ← Previous
                  </Button>
                  <span className="pagination-info">
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </span>
                  <Button
                    variant="ghost"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={!pagination.hasMore}
                    className="pagination-button"
                  >
                    Next →
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      ) : null}

      <div className="footer-links">
        <Link to="/">← Back to Home</Link>
        <Link to="/terms">Terms of Service</Link>
        <Link to="/privacy">Privacy Policy</Link>
      </div>

      <div className="watermark" aria-hidden="true">
        carnivoresadaptingterribly.com
      </div>
    </div>
  );
};

export default OfficialCardDirectory;
